import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EPAYMENT_TYPES_NAME, TITLE_INHERIT_STYLE } from "@constants";
import { IFinalizeOrderResponse, usePaymentBalance } from "./hook/usePaymentBalance";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { PaymentBalanceCardPay } from "./PaymentBalanceCardPay";
import { PaymentModal } from "@common/Modal/PaymentModal";
import { EJccFormTypes } from "../interface/IPayment";

interface IParams {
    accountId: string;
    openedJccForm?: EJccFormTypes;
    setOpenedJccForm(openedJccForm?: EJccFormTypes): void;
}

export const PaymentBalance = (props: IParams) => {
    const { accountId, openedJccForm, setOpenedJccForm } = props;
    const {
        balanceDetails,
        balanceState,
        getCurrentBalance,
        finaliseState,
        finaliseBalancePayment,
    } = usePaymentBalance({
        accountId,
    });
    const { t } = useTranslation(["payment", "common"]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalData, setModalData] = useState<IFinalizeOrderResponse>({
        success: false,
        orderNumber: "",
        paymentRef: "",
        file: {
            name: "",
            base64File: "",
        },
        prospectId: "",
    });

    const displayPaymentMethod =
        !balanceState.isLoading && !balanceState.error && balanceDetails && balanceDetails.overDueAmount > 0;

    const finalisePaymentCallback = (modalData: IFinalizeOrderResponse) => {
        setModalData(modalData);
        getCurrentBalance();
        setIsModalOpen(true);
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        const finalisePayment = async () => {
            const orderId = searchParams.get("orderId");
            const from = searchParams.get("from");

            if (orderId && from && from === "balance") {
                window.history.replaceState({}, document.title, window.location.href.split("?")[0]);
                finaliseBalancePayment(orderId, finalisePaymentCallback);
            }
        };

        finalisePayment();
    }, []);

    return (
        <>
            <Card elevation={2}>
                <CardHeader
                    className="card-header card-header__right"
                    title={t("payment:myCurrentBalance")}
                    titleTypographyProps={TITLE_INHERIT_STYLE}
                />
                <CardContent>
                    <div className="card-header__circle" />
                    <HandleErrorAndLoading
                        error={balanceState.error}
                        isLoading={balanceState.isLoading || finaliseState.isLoading}
                        retryFetch={getCurrentBalance}
                    >
                        {balanceDetails && (
                            <Fragment>
                                <Typography align="center" color="primary" gutterBottom variant="body1">
                                    {t("payment:dueBalance")}
                                </Typography>
                                <Typography align="center" color="primary" gutterBottom variant="h2">
                                    {t("common:price", { price: balanceDetails.overDueAmount })}
                                </Typography>
                                {balanceDetails.overDueAmount > 0 && (
                                    <>
                                        <Typography align="center" color="secondary" gutterBottom variant="body1">
                                            {t("payment:totalBalance")}:{" "}
                                            {t("common:price", { price: balanceDetails.totalAmount })}
                                        </Typography>
                                    </>
                                )}
                                {balanceDetails.overDueAmount > 0 && (
                                    <PaymentBalanceCardPay
                                        amountToPay={balanceDetails.overDueAmount}
                                        openedJccForm={openedJccForm}
                                        setOpenedJccForm={setOpenedJccForm}
                                    />
                                )}
                            </Fragment>
                        )}
                    </HandleErrorAndLoading>
                </CardContent>
                {displayPaymentMethod && (
                    <CardActions className="card-footer">
                        <Typography align="center" color="inherit" variant="body1">
                            {t("payment:paymentMethod")}: {t(`payment:${EPAYMENT_TYPES_NAME.CREDIT_CARD}` as any)}
                        </Typography>
                    </CardActions>
                )}
            </Card>
            <PaymentModal
                success={modalData.success}
                paymentRef={modalData.paymentRef}
                prospectId={modalData.prospectId}
                file={modalData.file}
                isModalOpen={isModalOpen}
                onConfirm={() => {
                    setIsModalOpen(false);
                }}
            />
        </>
    );
};
